import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router-dom";
import { IconPin } from "@tabler/icons-react";
import api from "@helpers/api";

import { useCommunity } from "@contexts/CommunityContext";

import CustomModal from "@components/CustomModal";

import {
    CommunityAssetImageField,
    AssetCategory,
    isValidHttpUrl,
} from "@features/uploadAsset";

import { Input, Text } from "@fragments";

function extractSectionEntity(res) {
    const status = res?.status ?? res?.response?.status;
    if (status != null && status >= 400) return null;
    const body = res?.data;
    if (!body) return null;
    return body.data ?? body;
}

export default function ConfigSessionModal({ modalRef }) {
    const { t } = useTranslation("contentModals");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadBusyCount, setUploadBusyCount] = useState(0);
    const [, setTick] = useState(0);

    const { loadCommunities } = useCommunity();

    const REF_config = useRef({});
    const REF_callback = useRef();

    const nav = useNavigate();

    const bump = () => setTick((n) => n + 1);

    const handleUploadingChange = (uploading) => {
        setUploadBusyCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    };

    modalRef.current = {
        open(preFill, callback) {
            REF_config.current = {
                ...preFill,
                name: preFill.name ?? "",
                description: preFill.description ?? { text: "" },
                banner: {
                    condensed: preFill.banner?.condensed ?? null,
                    wide: preFill.banner?.wide ?? null,
                },
                playlists: (preFill.content ?? []).filter((c) => !c?.single),
                fixed_playlist_id: preFill.fixed_playlist?.id ?? null,
            };
            REF_callback.current = callback;
            setOpen(true);
        },
    };

    async function handleSubmit() {
        try {
            setLoading(true);
            const payload = getPayload();
            const communityId = parseInt(REF_config.current.community, 10);
            const id = REF_config.current.id;

            let res;
            if (id) {
                res = await api.patch(`section/${id}`, payload);
            } else {
                res = await api.post(`community/${communityId}/section`, payload);
            }

            const data = extractSectionEntity(res);

            if (data?.id) {
                await REF_callback.current(data);
                await loadCommunities();
                setOpen(false);
            }
        } catch (e) {
            console.log(e);
        }

        setLoading(false);

        function getPayload() {
            const { name, description, banner, fixed_playlist_id } =
                REF_config.current;

            const condensed =
                banner?.condensed && isValidHttpUrl(banner.condensed)
                    ? banner.condensed
                    : null;
            const wide =
                banner?.wide && isValidHttpUrl(banner.wide) ? banner.wide : null;

            const payload = {
                name: name.trim(),
                description: description?.text?.trim() ?? null,
                banner: {
                    condensed,
                    wide,
                },
            };

            if (REF_config.current.id) {
                payload.fixed_playlist_id = fixed_playlist_id ?? null;
            }

            return payload;
        }
    }

    async function handleDelete() {
        try {
            const { data } = await api.delete("section/" + REF_config.current.id);
        } catch (e) {
            console.log(e);
        }

        nav(-1);

        loadCommunities();

        setOpen(false);
    }

    const communityId = REF_config.current.community
        ? parseInt(REF_config.current.community, 10)
        : null;

    return (
        <CustomModal
            maxWidth={500}
            bottomButtonSumbitText={t("manageSession.submitButton")}
            bottomButtonCancelText={t("common:actions.cancel")}
            loading={loading}
            disabled={uploadBusyCount > 0}
            handleDelete={REF_config.current?.id && handleDelete}
            deletionTitle={t("manageSession.deleteConfirmTitle")}
            title={REF_config.current?.id ? t("manageSession.editTitle") : t("manageSession.createTitle")}
            handleSubmit={handleSubmit}
            open={open}
            handleClose={() => setOpen(false)}
            bodyComponent={
                open && communityId ? (
                    <div className="px-4 py-1">
                        <div className="banner-add mt-2">
                            <span>{t("manageSession.desktopBanner")}</span>

                            <CommunityAssetImageField
                                communityId={communityId}
                                category={AssetCategory.SECTION_BANNER}
                                value={REF_config.current.banner?.wide}
                                onChange={(url) => {
                                    REF_config.current.banner = {
                                        ...REF_config.current.banner,
                                        wide: url,
                                    };
                                    bump();
                                }}
                                onUploadingChange={handleUploadingChange}
                                aspectRatio={16 / 4}
                                size={80}
                                imageMaxWidth={1700}
                                title={t("manageSession.bannerCropTitle")}
                            />
                        </div>
                        <div className="banner-add mt-2">
                            <span>{t("manageSession.mobileBanner")}</span>

                            <CommunityAssetImageField
                                communityId={communityId}
                                category={AssetCategory.SECTION_HERO}
                                value={REF_config.current.banner?.condensed}
                                onChange={(url) => {
                                    REF_config.current.banner = {
                                        ...REF_config.current.banner,
                                        condensed: url,
                                    };
                                    bump();
                                }}
                                onUploadingChange={handleUploadingChange}
                                aspectRatio={4 / 3}
                                size={120}
                                imageMaxWidth={1600}
                                title={t("manageSession.bannerCropTitle")}
                            />
                        </div>
                        <Input.Text
                            className="mt-4"
                            autoFocus={true}
                            placeholder={t("manageSession.namePlaceholder")}
                            value={REF_config.current.name}
                            onInput={(v) => {
                                REF_config.current.name = v;
                            }}
                            label={t("manageSession.nameLabel")}
                            size={50}
                        />
                        <Input.Text
                            className="mt-2"
                            multiline
                            placeholder={t("manageSession.descriptionPlaceholder")}
                            value={REF_config.current.description?.text}
                            onInput={(v) => {
                                REF_config.current.description = {
                                    ...REF_config.current.description,
                                    text: v,
                                };
                            }}
                            label={t("manageSession.descriptionLabel")}
                            size={250}
                        />

                        {REF_config.current?.id &&
                            REF_config.current.playlists?.length > 0 && (
                                <div className="mt-4">
                                    <Text.h4 left={<IconPin />}>
                                        {t("manageSession.fixedPlaylist.title")}
                                    </Text.h4>
                                    <Text.span contrast={6}>
                                        {t("manageSession.fixedPlaylist.description")}
                                    </Text.span>
                                    <Input.SelectOptions
                                        className="mt-2"
                                        label={t("manageSession.fixedPlaylist.selectLabel")}
                                        placeholder={t("manageSession.fixedPlaylist.selectPlaceholder")}
                                        options={REF_config.current.playlists.map(
                                            (p) => ({
                                                label: p.name,
                                                value: p.id,
                                            })
                                        )}
                                        value={
                                            REF_config.current.fixed_playlist_id
                                                ? [REF_config.current.fixed_playlist_id]
                                                : []
                                        }
                                        onInput={(values) => {
                                            REF_config.current.fixed_playlist_id =
                                                values.length > 0
                                                    ? values[values.length - 1]
                                                    : null;
                                            bump();
                                        }}
                                        maxItems={1}
                                    />
                                </div>
                            )}
                    </div>
                ) : null
            }
        />
    );
}
